import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Signal, computed, input } from '@angular/core';

import { PopularBetsOptionTeamInfo } from '@cds/betting-offer/domain-specific/popular-bets';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule } from '@frontend/sports/betting-offer/feature/short-team-names';
import { FontResizerComponent } from '@frontend/sports/common/core/feature/font-resizer';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { PopularBetsPodTypes } from '@frontend/sports/types/components/popular-bets';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { EventNameMapperService } from '../../../event-details-common/event-name-mapper.service';
import { GridModule } from '../../../grid/grid.module';
import { EventPickComponent } from '../../../option-pick/event-pick.component';
import { PopularBetsPodEventLogoComponent } from '../../popular-bets-pod-event-logo/popular-bets-pod-event-logo.component';
import { PopularBetsPodEventLogoService } from '../../popular-bets-services/popular-bets-pod-event-logo.service';
import { PopularBetsPodEventParticipantService } from '../../popular-bets-services/popular-bets-pod-event-participant.service';
import { PopularBetsPodOptionService } from '../../popular-bets-services/popular-bets-pod-option.service';

@Component({
    imports: [
        NgTemplateOutlet,
        FontResizerComponent,
        GridModule,
        EventPickComponent,
        PopularBetsPodEventLogoComponent,
        IconCustomComponent,
        NativePriceFormatterPipe,
    ],
    selector: 'ms-popular-bets-pod-event',
    templateUrl: './popular-bets-pod-event.component.html',
    styleUrls: ['./popular-bets-pod-event.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopularBetsPodEventComponent {
    @HostBinding('class') className = 'popular-bets-pod-event-connector';
    @HostBinding('class.odds-selection-hidden') get isOddsSelectionHidden(): boolean {
        return !this.showOptionPick();
    }

    event = input.required<GridEvent>();
    showOptionPick = input.required<boolean>();
    tracking = input.required<TrackingSource>();
    isEpcotEnabled = input.required<boolean>();
    podOptionTeamInfo = input.required<PopularBetsOptionTeamInfo[]>();

    private showOpponent = this.podOptionService.getOpponentEnabledByPodType(PopularBetsPodTypes.Parlay);
    private showMarketName = this.podOptionService.getMarketNameEnabledByPodType(PopularBetsPodTypes.Parlay);

    constructor(
        private eventNameMapper: EventNameMapperService,
        private eventLogoService: PopularBetsPodEventLogoService,
        private participantService: PopularBetsPodEventParticipantService,
        private podOptionService: PopularBetsPodOptionService,
    ) {}

    optionGroup: Signal<EventOptionGroup | null> = computed(() => this.event().optionGroups[0] ?? null);
    option: Signal<EventOption | null> = computed(() => this.optionGroup()?.options[0] ?? null);
    logoDetails = computed(() => {
        const option = this.option();
        if (option) {
            return this.eventLogoService.getLogoDetailsForPodEvent(option, this.event(), PopularBetsPodTypes.Parlay, this.podOptionTeamInfo());
        }
        return null;
    });

    opponentParticipant = computed(() => {
        const option = this.option();
        if (option) {
            return this.participantService.getOpponentParticipantFromOption(this.option()!, this.event(), this.podOptionTeamInfo());
        }
        return undefined;
    });

    eventTitle = computed(() => this.eventNameMapper.map(this.event(), false, ShortNameModule.PopularBets));

    get showSelectionInfo(): boolean {
        return this.showOpponent || this.showMarketName;
    }

    selectionInfo = computed(() => {
        const currentOption = this.option();
        if (!currentOption) {
            return '';
        }

        return this.podOptionService.getSelection(
            this.event(),
            currentOption.name,
            this.eventTitle(),
            PopularBetsPodTypes.Parlay,
            this.opponentParticipant(),
        );
    });
}
